import React from 'react';
import { SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import { ArcRowsTable, useArcRowsEditor } from '../../Common/ArcRows';
import { EditActions, RowEditorAlerts } from '../../Common/RowEditorControls';
import VersionHeader from '../../Common/VersionHeader';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { formatLocale, Locale } from '../../../utils/locales';
import * as baseRates from './baseRates';

interface BaseRatesTableProps {
  rates: baseRates.BaseRates;
  // Null until the locales have loaded; editing needs them.
  locales: Locale[] | null;
  // Rejects with the API error when the save fails.
  onSave: (basePrices: baseRates.BasePrice[]) => Promise<unknown>;
  onReload: () => void;
}

const BaseRatesTable = ({ rates, locales, onSave, onReload }: BaseRatesTableProps): JSX.Element => {
  const editor = useArcRowsEditor(baseRates.rateRows, rates.basePrices, locales, {
    emptyMessage: 'Add at least one base rate.',
    onSave,
  });

  const actions = (
    <EditActions
      editor={editor}
      ids={{
        edit: 'editBaseRates',
        add: 'addBaseRate',
        cancel: 'cancelBaseRates',
        save: 'saveBaseRates',
      }}
      ready={locales !== null}
      changed={baseRates.rateRows.hasChanges(editor.rows, rates.basePrices.length)}
    />
  );

  return (
    <SpaceBetween size="m">
      <RowEditorAlerts
        editor={editor}
        idPrefix="baseRates"
        conflictHeader={baseRates.CONFLICT_HEADER}
        onReload={onReload}
      />
      <ArcRowsTable
        id="baseRatesTable"
        list={baseRates.rateRows}
        editor={editor}
        locales={locales}
        display={formatLocale}
        rowErrorId="baseRateRowError"
        removeId="removeBaseRate"
        header={<VersionHeader title="Base rates" record={rates} actions={actions} />}
        empty="No base rates. Add a row to create one."
      />
    </SpaceBetween>
  );
};

export default BaseRatesTable;
